import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filtroGlobal, setFiltroGlobal] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [usuarioParaExcluir, setUsuarioParaExcluir] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    funcao: 'atendente',
    senha: '',
  });

  useEffect(() => {
    carregarUsuarios();
  }, []);

  const carregarUsuarios = async () => {
    try {
      const res = await axios.get(`${API}/usuarios`);
      setUsuarios(res.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/usuarios`, formData);
      toast.success('Usuário criado com sucesso!');
      setDialogOpen(false);
      resetForm();
      carregarUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleToggleAtivo = async (usuarioId, ativo) => {
    try {
      await axios.put(`${API}/usuarios/${usuarioId}?ativo=${!ativo}`);
      toast.success(`Usuário ${!ativo ? 'ativado' : 'desativado'} com sucesso!`);
      carregarUsuarios();
    } catch (error) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      funcao: 'atendente',
      senha: '',
    });
  };

  const handleDeleteClick = (usuario) => {
    setUsuarioParaExcluir(usuario);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!usuarioParaExcluir) return;
    try {
      await axios.delete(`${API}/usuarios/${usuarioParaExcluir.id}`);
      toast.success('Usuário excluído com sucesso!');
      carregarUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setDeleteDialogOpen(false);
      setUsuarioParaExcluir(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="usuarios-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-1 lg:mb-2">Usuários</h1>
          <p className="text-sm sm:text-base text-slate-600">Gerencie atendentes e gerentes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="criar-usuario-btn">
              <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" /> Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Novo Usuário</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome Completo *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome do usuário"
                  required
                  data-testid="input-nome"
                />
              </div>

              <div>
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  required
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label>Telefone *</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  required
                  data-testid="input-telefone"
                />
              </div>

              <div>
                <Label>Função *</Label>
                <Select
                  value={formData.funcao}
                  onValueChange={(value) => setFormData({ ...formData, funcao: value })}
                >
                  <SelectTrigger data-testid="select-funcao">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="atendente">Atendente</SelectItem>
                    <SelectItem value="gerente">Gerente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Senha *</Label>
                <Input
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="input-senha"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="salvar-usuario-btn">
                  Criar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campo de busca */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar usuários..."
            value={filtroGlobal}
            onChange={(e) => setFiltroGlobal(e.target.value)}
            className="pl-10 text-sm"
          />
        </div>
      </div>

      {/* Layout Mobile - Cards */}
      <div className="lg:hidden space-y-4">
        {usuarios.filter(u => 
          u.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
          u.email?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
          u.telefone?.includes(filtroGlobal)
        ).length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center text-slate-500">
            {filtroGlobal ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
          </div>
        ) : (
          usuarios.filter(u => 
            u.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
            u.email?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
            u.telefone?.includes(filtroGlobal)
          ).map((usuario) => (
            <div key={usuario.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800 text-sm">{usuario.nome}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{usuario.email}</p>
                </div>
                <Badge variant={usuario.funcao === 'gerente' ? 'default' : 'secondary'} className="text-xs">
                  {usuario.funcao}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Telefone:</span>
                  <span className="font-medium text-slate-800">{usuario.telefone}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Status:</span>
                  <Badge variant={usuario.ativo ? 'default' : 'destructive'} className="text-xs">
                    {usuario.ativo ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-slate-600">Ativar/Desativar:</span>
                  <Switch
                    checked={usuario.ativo}
                    onCheckedChange={() => handleToggleAtivo(usuario.id, usuario.ativo)}
                  />
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteClick(usuario)}
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Layout Desktop - Tabela */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs xl:text-sm">Nome</TableHead>
              <TableHead className="text-xs xl:text-sm">E-mail</TableHead>
              <TableHead className="text-xs xl:text-sm hidden xl:table-cell">Telefone</TableHead>
              <TableHead className="text-xs xl:text-sm">Função</TableHead>
              <TableHead className="text-xs xl:text-sm hidden xl:table-cell">Status</TableHead>
              <TableHead className="text-xs xl:text-sm">Ativo</TableHead>
              <TableHead className="text-xs xl:text-sm w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usuarios.filter(u => 
              u.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
              u.email?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
              u.telefone?.includes(filtroGlobal)
            ).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  {filtroGlobal ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
                </TableCell>
              </TableRow>
            ) : (
              usuarios.filter(u => 
                u.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
                u.email?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
                u.telefone?.includes(filtroGlobal)
              ).map((usuario) => (
                <TableRow key={usuario.id} data-testid={`usuario-row-${usuario.id}`}>
                  <TableCell className="font-medium text-xs xl:text-sm">{usuario.nome}</TableCell>
                  <TableCell className="text-xs xl:text-sm">{usuario.email}</TableCell>
                  <TableCell className="text-xs xl:text-sm hidden xl:table-cell">{usuario.telefone}</TableCell>
                  <TableCell>
                    <Badge variant={usuario.funcao === 'gerente' ? 'default' : 'secondary'} className="text-xs">
                      {usuario.funcao}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <Badge variant={usuario.ativo ? 'default' : 'destructive'} className="text-xs">
                      {usuario.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={usuario.ativo}
                      onCheckedChange={() => handleToggleAtivo(usuario.id, usuario.ativo)}
                      data-testid={`toggle-usuario-${usuario.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(usuario)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-usuario-${usuario.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Tem certeza que deseja excluir <strong>{usuarioParaExcluir?.nome}</strong>?</p>
            <p className="text-sm text-red-600 mt-2">Esta ação não pode ser desfeita.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}